import { useState, useMemo } from 'react';
import {
  useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel,
  createColumnHelper, flexRender,
  type SortingState, type ColumnFiltersState,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, ChevronsUpDown, Mail } from 'lucide-react';
import type { DispatchLogRow } from '../api-client';

// ── Sub-components ──────────────────────────────────────────────────────────

function SortIcon({ state }: { state: false | 'asc' | 'desc' }) {
  if (state === 'asc') return <ChevronUp className="w-3 h-3 text-blueberry-500" />;
  if (state === 'desc') return <ChevronDown className="w-3 h-3 text-blueberry-500" />;
  return <ChevronsUpDown className="w-3 h-3 text-nightshade-300 dark:text-nightshade-600" />;
}

function ColumnFilter({ values, value, onChange }: {
  values: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  if (values.length === 0) return null;
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      className="mt-1 w-full text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded px-1.5 py-0.5 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-1 focus:ring-blueberry-400 cursor-pointer"
    >
      <option value="">All</option>
      {values.map(v => <option key={v} value={v}>{v}</option>)}
    </select>
  );
}

function LaunchResultChip({ result }: { result: string }) {
  if (result === 'queued' || result === 'processing') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-400 dark:text-nightshade-500 border border-nightshade-200 dark:border-nightshade-700">
        {result === 'queued' ? 'Queued' : 'Processing'}
      </span>
    );
  }
  if (result === 'sent') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blueberry-50 dark:bg-blueberry-900/20 text-blueberry-600 dark:text-blueberry-400 border border-blueberry-200 dark:border-blueberry-800">
        Sent to Clay
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-pomegranate-100 dark:bg-pomegranate-900/30 text-pomegranate-700 dark:text-pomegranate-400 border border-pomegranate-200 dark:border-pomegranate-800">
      {result === 'skipped' ? 'Skipped' : 'Failed'}
    </span>
  );
}

function ClayResultChip({ resultStatus }: { resultStatus: string | null }) {
  if (!resultStatus) {
    return <span className="text-nightshade-300 dark:text-nightshade-600">—</span>;
  }
  const isError = /error|invalid/i.test(resultStatus);
  const chipClass = isError
    ? 'bg-pomegranate-100 dark:bg-pomegranate-900/30 text-pomegranate-700 dark:text-pomegranate-400 border border-pomegranate-200 dark:border-pomegranate-800'
    : 'bg-tangerine-50 dark:bg-tangerine-900/20 text-tangerine-700 dark:text-tangerine-400 border border-tangerine-200 dark:border-tangerine-800';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${chipClass}`}>
      {resultStatus}
    </span>
  );
}

function CopyIndicator({ row }: { row: DispatchLogRow }) {
  const hasSentCopy = row.subject || row.body;
  const hasFinalCopy = row.result_data?.final_subject || row.result_data?.final_body;
  if (!hasSentCopy && !hasFinalCopy) {
    return <span className="text-nightshade-300 dark:text-nightshade-600">—</span>;
  }
  return (
    <Mail className="w-3.5 h-3.5 text-blueberry-400 dark:text-blueberry-500" />
  );
}

// ── Column helper ─────────────────────────────────────────────────────────

const col = createColumnHelper<DispatchLogRow>();

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ── Filterable column IDs ─────────────────────────────────────────────────

const FILTER_COLUMNS = ['bucket', 'status_column', 'result', 'result_status'] as const;

// ── Main component ──────────────────────────────────────────────────────────

interface DispatchLogTableProps {
  rows: DispatchLogRow[];
  isLoading: boolean;
  selectedRowId?: string | null;
  onSelectRow?: (row: DispatchLogRow) => void;
}

export function DispatchLogTable({ rows, isLoading, selectedRowId, onSelectRow }: DispatchLogTableProps) {
  const [modeFilter, setModeFilter] = useState<'all' | 'live' | 'test'>('all');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Pre-filter by live/test mode
  const modeFiltered = useMemo(() => {
    if (modeFilter === 'live') return rows.filter(r => !r.is_test);
    if (modeFilter === 'test') return rows.filter(r => r.is_test);
    return rows;
  }, [rows, modeFilter]);

  // Unique values for column filters
  const uniqueBuckets = useMemo(() =>
    Array.from(new Set(rows.map(r => r.bucket).filter(Boolean))).sort() as string[], [rows]);
  const uniqueStatuses = useMemo(() =>
    Array.from(new Set(rows.map(r => r.status_column).filter(Boolean))).sort() as string[], [rows]);
  const uniqueResults = useMemo(() =>
    Array.from(new Set(rows.map(r => r.result).filter(Boolean))).sort() as string[], [rows]);
  const uniqueClayResults = useMemo(() =>
    Array.from(new Set(rows.map(r => r.result_status).filter(Boolean))).sort() as string[], [rows]);

  const filterOptionsMap: Record<string, string[]> = {
    bucket: uniqueBuckets,
    status_column: uniqueStatuses,
    result: uniqueResults,
    result_status: uniqueClayResults,
  };

  const columns = useMemo(() => [
    col.accessor('contact_name', {
      header: 'Name',
      cell: info => (
        <span className="font-medium text-nightshade-800 dark:text-nightshade-200 truncate block max-w-[160px]">
          {info.getValue() ?? '—'}
        </span>
      ),
      enableColumnFilter: false,
    }),
    col.accessor('contact_email', {
      header: 'Email',
      cell: info => (
        <span className="text-nightshade-500 dark:text-nightshade-400 truncate block max-w-[180px]">
          {info.getValue() ?? '—'}
        </span>
      ),
      enableColumnFilter: false,
    }),
    col.accessor('bucket', {
      header: 'Bucket',
      cell: info => {
        const val = info.getValue();
        const error = info.row.original.error;
        return (
          <span className="text-nightshade-600 dark:text-nightshade-300">
            {val ?? (error ? <span className="text-nightshade-400 italic">{error}</span> : '—')}
          </span>
        );
      },
      filterFn: 'equals',
    }),
    col.accessor('status_column', {
      header: 'Status',
      cell: info => (
        <span className="text-nightshade-500 dark:text-nightshade-400">{info.getValue() ?? '—'}</span>
      ),
      filterFn: 'equals',
    }),
    col.accessor('dispatched_at', {
      header: 'Sent at',
      cell: info => (
        <span className="text-nightshade-400 whitespace-nowrap">
          {formatDate(info.getValue())}
          {info.row.original.is_test && (
            <span className="ml-1 text-[10px] text-blueberry-500">(test)</span>
          )}
        </span>
      ),
      enableColumnFilter: false,
    }),
    col.accessor('result', {
      header: 'Launch',
      cell: info => <LaunchResultChip result={info.getValue()} />,
      filterFn: 'equals',
    }),
    col.accessor('result_status', {
      header: 'Clay Result',
      cell: info => <ClayResultChip resultStatus={info.getValue()} />,
      filterFn: 'equals',
    }),
    col.display({
      id: '_copy',
      header: 'Copy',
      cell: info => <CopyIndicator row={info.row.original} />,
    }),
  ], []);

  const table = useReactTable({
    data: modeFiltered,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const visibleRows = table.getRowModel().rows;

  return (
    <div className="space-y-3">
      {/* Mode filter + row count */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex rounded-full bg-nightshade-100 dark:bg-nightshade-800 p-0.5 text-xs">
          {(['all', 'live', 'test'] as const).map(f => (
            <button
              key={f}
              onClick={() => setModeFilter(f)}
              className={`px-3 py-1.5 capitalize transition-colors rounded-full ${
                modeFilter === f
                  ? 'bg-nightshade-900 dark:bg-white text-white dark:text-nightshade-900 font-medium'
                  : 'text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200'
              }`}
            >
              {f === 'all' ? 'All' : f === 'live' ? 'Live only' : 'Test only'}
            </button>
          ))}
        </div>
        <span className="text-xs text-nightshade-400 ml-auto">{visibleRows.length} rows</span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-5 h-5 rounded-full border-2 border-blueberry-400 border-t-transparent animate-spin" />
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="rounded-2xl border border-nightshade-200 dark:border-nightshade-700 py-10 text-center">
          <p className="text-sm text-nightshade-400 italic">No dispatches yet</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-nightshade-200 dark:border-nightshade-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id} className="bg-nightshade-100 dark:bg-nightshade-800 backdrop-blur-sm border-b border-nightshade-200 dark:border-nightshade-700">
                    {hg.headers.map(header => {
                      const canSort = header.column.getCanSort();
                      const colId = header.column.id;
                      const filterOpts = filterOptionsMap[colId] ?? [];
                      const currentFilter = (columnFilters.find(f => f.id === colId)?.value as string) ?? '';
                      const showFilter = (FILTER_COLUMNS as readonly string[]).includes(colId) && filterOpts.length > 0;

                      return (
                        <th key={header.id} className="text-left px-3 py-2 text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 whitespace-nowrap">
                          <div
                            className={`flex items-center gap-1 ${canSort ? 'cursor-pointer select-none hover:text-nightshade-700 dark:hover:text-nightshade-300 transition-colors' : ''}`}
                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && <SortIcon state={header.column.getIsSorted()} />}
                          </div>
                          {showFilter && (
                            <ColumnFilter
                              values={filterOpts}
                              value={currentFilter}
                              onChange={v => {
                                setColumnFilters(prev => {
                                  const without = prev.filter(f => f.id !== colId);
                                  return v ? [...without, { id: colId, value: v }] : without;
                                });
                              }}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {visibleRows.map((row, i) => {
                  const original = row.original;
                  const isLast = i === visibleRows.length - 1;
                  const isSelected = selectedRowId === original.id;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => onSelectRow?.(original)}
                      className={`
                        ${!isLast ? 'border-b border-nightshade-100 dark:border-nightshade-800' : ''}
                        ${original.result === 'skipped' ? 'opacity-50' : ''}
                        ${isSelected ? 'bg-blueberry-50/50 dark:bg-blueberry-900/20' : 'hover:bg-nightshade-50 dark:hover:bg-nightshade-800/40'}
                        ${onSelectRow ? 'cursor-pointer' : ''}
                        transition-colors
                      `}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-3 py-2">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
